import { InstalledUpiApp } from '../installedUpiIntents'
import { InvestPaymentMethodType, InvestPaymentMethodTypes } from '../../types/mf-execution.types'
import { mfLogoBaseUrl } from '../../env'
import {
  findMatchedUpiMethod,
  getNetBankingSelectionId,
  getUpiAppSelectionId,
} from './paymentMethodSelection'

/** Shared helpers for PaymentMethodsScreen and AutopayMethodsScreen (bank logos, selection ids). */

/**
 * Resolves bank accounts shown under a selected UPI app.
 * Prefers explicit `bankAccountDetails` from the API; otherwise derives a single
 * linked account from payment methods when only UPI/net-banking metadata exists.
 */
export function getLinkedBankAccounts(
  bankAccountDetails: InvestPaymentMethodType[] | undefined,
  app: InstalledUpiApp,
  paymentMethods: InvestPaymentMethodType[] | undefined
): InvestPaymentMethodType[] {
  if (bankAccountDetails?.length) {
    return bankAccountDetails
  }

  const linkedBank = getLinkedBankForUpiApp(app, paymentMethods)
  if (!linkedBank?.displayAccNumber) return []

  return [
    {
      displayAccNumber: linkedBank.displayAccNumber,
      logoUrl: linkedBank.logoPath,
    } as InvestPaymentMethodType,
  ]
}

/**
 * Builds an image URL for a bank logo, using the MF CDN base path for relative paths
 * and a shared grayscale fallback when no logo is available.
 */
export function getBankLogoSrc(logoPath?: string): string {
  if (!logoPath) {
    return '/images/mutual_fund/categories/banks_grayscale.svg'
  }

  return logoPath.startsWith('http') ? logoPath : `${mfLogoBaseUrl}${logoPath}`
}

/**
 * Finds the display account and logo to associate with an installed UPI app.
 * Matches the app to a UPI payment method first, then falls back to the first
 * UPI or net-banking method that exposes a display account number.
 */
export function getLinkedBankForUpiApp(
  app: InstalledUpiApp,
  paymentMethods: InvestPaymentMethodType[] | undefined
): { displayAccNumber: string; logoPath?: string } | null {
  const methods = paymentMethods ?? []
  const matchedUpiMethod = findMatchedUpiMethod(app, methods)
  const firstUpiMethod = methods.find((method) => method?.type === InvestPaymentMethodTypes.UPI)
  const firstNetBankingMethod = methods.find((method) => method?.type === InvestPaymentMethodTypes.NET_BANKING)
  const linkedNetBankingMethod = methods.find(
    (method) =>
      method?.type === InvestPaymentMethodTypes.NET_BANKING &&
      method?.accountNumber &&
      method?.accountNumber === matchedUpiMethod?.accountNumber
  )

  let bankMethod: InvestPaymentMethodType | undefined
  if (matchedUpiMethod?.displayAccNumber) {
    bankMethod = matchedUpiMethod
  } else if (firstUpiMethod?.displayAccNumber) {
    bankMethod = firstUpiMethod
  } else if (firstNetBankingMethod?.displayAccNumber) {
    bankMethod = firstNetBankingMethod
  }

  if (!bankMethod?.displayAccNumber) return null

  return {
    displayAccNumber: bankMethod.displayAccNumber,
    logoPath: bankMethod.logoUrl ?? linkedNetBankingMethod?.logoUrl ?? firstNetBankingMethod?.logoUrl,
  }
}

/**
 * Maps the currently selected payment method to a stable row id used by the screen.
 * Net banking uses payment-method ids; UPI intents use installed-app ids derived from
 * package name or a synthetic id when the app list is not yet available.
 */
export function getSelectionIdFromPaymentMethod(
  method: InvestPaymentMethodType | null,
  installedUpiApps?: InstalledUpiApp[] | null
): string | null {
  if (!method) return null

  if (method?.type === InvestPaymentMethodTypes.NET_BANKING) {
    return getNetBankingSelectionId(method)
  }

  if (method?.type === InvestPaymentMethodTypes.UPI || method?.type === InvestPaymentMethodTypes.UPI_INTENT) {
    const packageName = method?.upiPackageName?.toLowerCase() ?? ''

    if (packageName && installedUpiApps?.length) {
      const matchedApp = installedUpiApps.find((app) => {
        const appPackageName = app?.packageName?.toLowerCase() ?? ''
        const appName = app?.name?.toLowerCase() ?? ''
        return appPackageName === packageName || appPackageName.includes(packageName) || appName.includes(packageName)
      })

      if (matchedApp) {
        return getUpiAppSelectionId(matchedApp)
      }
    }

    if (method?.upiPackageName) {
      return getUpiAppSelectionId({ name: '', packageName: method.upiPackageName, appLogoUrl: '' })
    }
  }

  return null
}
